import classNames from 'classnames';
import Immutable from 'immutable';
import SSIDragHotspot from 'modules/SSIAnchors/components/SSIDragHotspot';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ArtboardLayoutContainerList from 'components/ArtboardLayoutContainerList';
import HeightExceedingIndicator from 'components/HeightExceedingIndicator';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import LayoutDragHotspot from 'components/LayoutDragHotspot';
import LayoutPreventDrop from 'components/LayoutPreventDrop';
import Toolbar from 'components/Toolbar/Section';
import Tooltip from 'components/Tooltip';
import { isVeevaMedcomms, ProjectsConfig } from 'const';
import { updateSection } from 'containers/Sections/actions';
import * as SectionModels from 'containers/Sections/models';
import * as Models from 'models';
import { isMockDocument } from 'utils/documents';
import { intlGet } from 'utils/intlGet';
import { isGroupLayout } from 'utils/layouts/isGroupLayout';
import { isReusableLayout } from 'utils/reusableLayouts/isReusableLayout';
import { isFixedSection } from 'utils/screenDefinitions/isFixedSection';
import { toPx } from 'utils/toPx';
import Carousel from '../Carousel/Carousel';
import { useSectionDropTarget } from './dnd';
import { createElementsForRender } from './helpers';
import { useSection } from './hooks';
import { sectionSelector } from './selectors';
import styles from './styles.module.scss';
import { useStyles } from './useStyles';

type Props = {
  layouts: Immutable.List<Models.CombinedLayoutMap>;
  section: Models.ExtendedSectionMap;
  sectionHeight: number;
  getLayoutsRefs: () => Immutable.Map<string, HTMLDivElement>;
  saveLayoutRef: (layoutId: string, element: HTMLDivElement) => void;
  dataTestId: number;
};

const Section: React.FC<Props> = (props) => {
  const { section, getLayoutsRefs, layouts, saveLayoutRef, sectionHeight, dataTestId } = props;
  const {
    activeLayoutIds,
    artboardLayoutIds,
    documentIdsAreBeingUploaded,
    documents,
    dragHotspotGroupLayoutId,
    dragHotspotPosition,
    projectMetadataTokenExistanceBySectionId,
    moveableLayoutId,
    relationsByRegularLayoutId,
    ssiHotspotPosition,
    projectType,
    colors,
    allLayouts,
    isReadOnly,
    isOpenToolbar,
    isResizingColumn,
    isResizingLayout,
    isResizingRow,
    dragSourceType,
    isDraggingAsset,
  } = useSelector(sectionSelector);

  const sectionDisplayName = section.get('displayName') ?? '';
  const sectionId = section.get('id');
  const hasProjectMetadataToken = projectMetadataTokenExistanceBySectionId.get(sectionId, false);
  const { connectDropTarget, isOver, canDrop } = useSectionDropTarget(sectionId, sectionDisplayName, hasProjectMetadataToken);

  const {
    sectionRef,
    fullContentRef,
    editMode,
    sectionHintRef,
    enableEditMode,
    canExpand,
    isMouseOver,
    onMouseLeave,
    onMouseEnter,
    onMouseOver,
    isEditMode,
    isExpand,
    toggleExpand,
    isTooltipShown,
    showTooltip,
    hideTooltip,
    isDraggingSSI,
    isDraggingLayout,
    isHighlighted,
    getSSIDragHotspotPosition,
    getLayoutDragHotspotPosition,
    saveAsGroupLayout,
  } = useSection({
    allLayouts,
    artboardLayoutIds,
    layouts,
    section,
    sectionHeight,
    ssiHotspotPosition,
    getLayoutsRefs,
    isOpenToolbar,
    isResizingColumn,
    isResizingLayout,
    isResizingRow,
    dragSourceType,
    isDraggingAsset,
  });

  const dispatch = useDispatch();

  const onStylesUpdate: SectionModels.ActionCreator.UpdateSection = (...args) => {
    return dispatch(updateSection(...args));
  };
  const [sectionStyles, sectionStylesSetters, sectionCSS] = useStyles(
    section as unknown as Models.SectionMap,
    { colors, images: documents },
    ProjectsConfig[projectType],
    editMode,
    onStylesUpdate,
  );

  const inlineSectionStyles = {
    height: isExpand ? 'auto' : toPx(sectionHeight),
    ['--parent-height']: isExpand ? 'auto' : toPx(sectionHeight),
    ...sectionCSS,
  };

  const onDragMove = ({ clientX, clientY }: React.MouseEvent): void => {
    if (!isOver) {
      return;
    }

    getLayoutDragHotspotPosition(clientY);
    getSSIDragHotspotPosition(clientX, clientY);
  };
  const elementsForRender = createElementsForRender(layouts);

  const renderContent = (): JSX.Element => {
    return (
      <>
        {elementsForRender.map((element, index) => {
          if (element.isCarousel && element.carouselId) {
            return (
              <Carousel
                getLayoutDragHotspotPosition={getLayoutDragHotspotPosition}
                key={`Carousel-${element.carouselId}`}
                carouselId={element.carouselId}
                documents={documents}
                isSectionActive={isMouseOver}
                isReadOnly={isOver && !canDrop}
                layouts={element.layouts}
                moveableLayoutId={moveableLayoutId}
                relationsByRegularLayoutId={relationsByRegularLayoutId}
                saveLayoutRef={saveLayoutRef}
                section={section}
                layoutIds={artboardLayoutIds}
                sectionDisplayName={sectionDisplayName}
              />
            );
          }

          return (
            <ArtboardLayoutContainerList
              key={`ArtboardLayoutContainerList-${index}`}
              documents={documents}
              getLayoutsRefs={getLayoutsRefs}
              isSectionActive={isMouseOver}
              layoutIds={artboardLayoutIds}
              isReadOnly={isOver && !canDrop}
              layouts={element.layouts}
              moveableLayoutId={moveableLayoutId}
              relationsByRegularLayoutId={relationsByRegularLayoutId}
              saveLayoutRef={saveLayoutRef}
              section={section}
              ssi={null}
              dataTestId={`section${dataTestId}`}
            />
          );
        })}
      </>
    );
  };

  const renderLayoutPreview = (): JSX.Element | null => {
    if (!isOver) {
      return null;
    }
    if (!isDraggingLayout()) {
      return null;
    }
    if (dragHotspotGroupLayoutId) {
      return null;
    }

    return canDrop ? <LayoutDragHotspot position={dragHotspotPosition} /> : <LayoutPreventDrop position={dragHotspotPosition} />;
  };

  const renderLayoutDragHotspot = (): JSX.Element | null => {
    if (!isOver) {
      return null;
    }
    if (!isDraggingLayout()) {
      return null;
    }
    if (dragHotspotGroupLayoutId) {
      return null;
    }

    return <LayoutDragHotspot position={dragHotspotPosition} />;
  };

  const renderSSIDragHotspot = (): JSX.Element | null => {
    if (!isOver) {
      return null;
    }
    if (!isDraggingSSI()) {
      return null;
    }
    if (!ssiHotspotPosition) {
      return null;
    }

    return <SSIDragHotspot position={ssiHotspotPosition} />;
  };

  const renderSectionLabel = (): JSX.Element | null => {
    return (
      <div className={styles.sectionLabel}>
        <span className={styles.sectionLabelText}>{sectionDisplayName}</span>
      </div>
    );
  };

  const renderSectionHint = (): JSX.Element | null => {

    const iconType = isExpand ? IconType.CHEVRON_UP : IconType.CHEVRON_DOWN;

    const groupLayout = layouts.size === 1 && isGroupLayout(layouts.first()) ? layouts.first() as Models.GroupLayoutMap : null;
    const groupLayoutDocument = groupLayout && documents.get(groupLayout.get('documentId'));
    const layoutsToCheck = groupLayout ? groupLayout.get('layoutIds').map(layoutId => allLayouts.get(layoutId)) : layouts;
    if (groupLayout) {
      const missingLayoutIds = groupLayout.get('layoutIds').filter(layoutId => !allLayouts.get(layoutId));
      if (missingLayoutIds.size > 0) {
        // eslint-disable-next-line no-console
        console.error(groupLayout.toJS(), `layouts missing from store: [${missingLayoutIds.join(', ')}]`);
      }
    }
    const isSaveEnabled = (
      layoutsToCheck.every(
        layout =>
          layout &&
          isReusableLayout(layout, documents) &&
          !documentIdsAreBeingUploaded.includes((layout as unknown as Models.BaseLayoutMap).get('documentId')),
      )
      && (!groupLayoutDocument || isMockDocument(groupLayoutDocument))
    );
    if (!isHighlighted()) {
      return null;
    }

    return (
      <div className={styles.hintWrapper} ref={sectionHintRef}>
        <div className={styles.hint}>
          {!isVeevaMedcomms() && <span className={styles.hintTitle}>{sectionDisplayName}</span>}
          <Icon
            className={isVeevaMedcomms() ? styles.firstActionIcon : styles.actionIcon}
            type={IconType.SETTINGS}
            size="badge"
            color="grey"
            onClick={enableEditMode}
          />
          <Icon
            className={styles.actionIcon}
            type={IconType.SAVE_LAYOUT}
            size="badge"
            color="grey"
            isDisabled={!isSaveEnabled}
            onClick={saveAsGroupLayout}
          />

          {
            canExpand &&
            <>
              <span className={classNames(styles.warning, styles.actionIcon)}>
                <Icon type={IconType.EXCLAMATION_MARK} size="badge" color="error" onMouseEnter={showTooltip} onMouseLeave={hideTooltip} />
                <Tooltip className={styles.tooltip} show={isTooltipShown} text={intlGet('Section', 'Warning')} />
              </span>
              <Icon className={styles.actionIcon} type={iconType} size="xs" color="grey" onClick={toggleExpand} />
            </>
          }
        </div>
        {editMode && <Toolbar
          name={sectionDisplayName}
          styles={sectionStyles}
          stylesSetters={sectionStylesSetters}
          colors={colors}
        />}
      </div>
    );
  };

  const renderFlexibleSection = (): React.ReactElement | null => connectDropTarget((
    <div
      className={classNames(styles.Section, { [styles.Section__highlighted]: isHighlighted() })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseOver={onMouseOver}
      onDragOver={onDragMove}
      style={sectionCSS}
      data-testid={`section${dataTestId}`}
    >
      {isVeevaMedcomms() && renderSectionLabel()}
      <div className={styles.content}>
        {renderLayoutPreview()}
        {renderContent()}
        {!isReadOnly && renderSectionHint()}
      </div>
    </div>
  ));
  const renderFixedSection = (): React.ReactElement | null => {
    return connectDropTarget((
      <div
        className={classNames(styles.Section, {
          [styles.Section__highlighted]: isHighlighted(),
          [styles.mouseOver]: isMouseOver,
        })}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseOver={onMouseOver}
        onDragOver={onDragMove}
        ref={sectionRef}
        style={inlineSectionStyles}
        data-testid={`section${dataTestId}`}
      >
        {isVeevaMedcomms() && renderSectionLabel()}
        {renderSSIDragHotspot()}
        {renderSectionHint()}
        <div
          className={classNames(styles.visibleContent, {
            [styles.visibleContent__full]: isExpand || !canExpand,
            [styles.increased]: activeLayoutIds.size !== 0,
          })}
        >
          <div
            ref={fullContentRef}
            className={styles.fullContent}
            onDoubleClick={isEditMode}
            style={{ minHeight: toPx(sectionHeight) }}
          >
            {renderLayoutDragHotspot()}
            {renderContent()}
            <HeightExceedingIndicator
              offsetTop={sectionHeight}
              isShown={isExpand}
            />
          </div>
        </div>
      </div>
    ));
  };

  if (isFixedSection(section)) {
    return renderFixedSection();
  }

  return renderFlexibleSection();
};

export default Section;
